import { useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  App,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { post, put } from '../../../api/client';
import {
  CHANNEL_SPECS,
  CHANNEL_TYPE_OPTIONS,
  type ChannelType,
  type ChannelFieldSpec,
} from '../channel-schema';
import type { AdminChannelView } from '../types';

interface Props {
  open: boolean;
  /** 编辑目标；为 null 表示新建 */
  editing: AdminChannelView | null;
  /** 新建时的默认排序值（现有渠道数） */
  defaultSortOrder: number;
  onClose: () => void;
  onSaved: () => void;
}

/** 所有渠道类型的字段键合集（切换类型时用于清理残留值） */
const ALL_FIELD_KEYS = (Object.keys(CHANNEL_SPECS) as ChannelType[]).flatMap((t) =>
  CHANNEL_SPECS[t].fields.map((f) => f.key),
);

/**
 * 支付渠道新建 / 编辑弹窗（脱敏密钥表单）。
 *
 * - 字段按渠道类型动态渲染，分为「展示配置（config，可回显编辑）」与「密钥（secret，只写不回显）」。
 * - 编辑时密钥输入框一律留空：placeholder 提示已配置与否，留空提交则后端按字段保留原值；
 *   仅当填入新值才覆盖对应密钥（修复了旧版整块覆盖会清空其它密钥的缺陷）。
 * - 渠道类型创建后不可修改（不同类型 config/secret 结构不同，改类型请删除后重建）。
 */
export function ChannelFormModal({ open, editing, defaultSortOrder, onClose, onSaved }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const watchedType = Form.useWatch('channelType', form) as ChannelType | undefined;

  const isEdit = editing !== null;
  const type = (watchedType ?? (editing?.channelType as ChannelType) ?? 'alipay') as ChannelType;
  const spec = CHANNEL_SPECS[type];

  // 已配置密钥的键名（编辑时用于提示，绝不回显值）
  const configuredSecrets = useMemo(() => new Set(editing?.secretKeys ?? []), [editing]);

  // 打开时初始化表单值
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const cfg = (editing.config ?? {}) as Record<string, unknown>;
      const initial: Record<string, unknown> = {
        channelType: editing.channelType,
        channelName: editing.channelName,
        isActive: editing.isActive,
        sortOrder: editing.sortOrder,
        remark: editing.remark ?? '',
      };
      // 仅回填非敏感 config 字段；secret 字段一律留空（只写不回显）
      for (const f of CHANNEL_SPECS[editing.channelType as ChannelType]?.fields ?? []) {
        if (f.store === 'config') initial[f.key] = cfg[f.key] ?? '';
      }
      form.setFieldsValue(initial);
    } else {
      form.resetFields();
      form.setFieldsValue({
        channelType: 'alipay',
        isActive: true,
        sortOrder: defaultSortOrder,
      });
    }
    // 仅在弹窗开合 / 编辑目标变化时重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? put(`/admin/payment-channels/${editing.id}`, payload) : post('/admin/payment-channels', payload),
    onSuccess: () => {
      message.success(editing ? '渠道已更新' : '渠道已创建');
      onSaved();
      onClose();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const activeSpec = CHANNEL_SPECS[values.channelType as ChannelType];
      const config: Record<string, unknown> = {};
      const secrets: Record<string, unknown> = {};
      for (const f of activeSpec.fields) {
        const raw = values[f.key];
        if (f.store === 'config') {
          config[f.key] = raw ?? '';
        } else if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
          // 仅提交填入的新密钥；留空则由后端保留原值
          secrets[f.key] = String(raw).trim();
        }
      }
      save.mutate({
        channelType: values.channelType,
        channelName: values.channelName,
        isActive: values.isActive ?? true,
        sortOrder: values.sortOrder ?? 0,
        remark: values.remark?.trim() ? values.remark.trim() : null,
        config,
        ...(Object.keys(secrets).length ? { secrets } : {}),
      });
    } catch {
      /* 校验失败，AntD 已高亮报错字段 */
    }
  };

  const renderField = (f: ChannelFieldSpec) => {
    const configured = f.store === 'secret' && configuredSecrets.has(f.key);
    const placeholder =
      f.store === 'secret'
        ? configured
          ? '已配置 · 留空保持不变'
          : f.placeholder ?? '请输入（加密存储）'
        : f.placeholder;

    let control: React.ReactNode;
    if (f.input === 'textarea') {
      control = <Input.TextArea rows={f.store === 'secret' ? 3 : 2} placeholder={placeholder} autoComplete="off" />;
    } else if (f.input === 'password') {
      control = <Input.Password placeholder={placeholder} autoComplete="new-password" visibilityToggle />;
    } else {
      control = <Input placeholder={placeholder} autoComplete="off" />;
    }

    return (
      <Col key={f.key} span={24}>
        <Form.Item
          name={f.key}
          label={
            <Space size={6}>
              <span>{f.label}</span>
              {f.store === 'secret' && (
                <Tag color={configured ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>
                  {configured ? '已配置' : '未配置'}
                </Tag>
              )}
            </Space>
          }
          tooltip={f.tip}
          rules={f.required ? [{ required: true, message: `请填写${f.label}` }] : undefined}
          extra={
            f.store === 'secret'
              ? '敏感密钥经 AES-256-GCM 加密存储，只写不回显；编辑时留空表示沿用原值。'
              : undefined
          }
        >
          {control}
        </Form.Item>
      </Col>
    );
  };

  return (
    <Modal
      title={isEdit ? `编辑渠道 · ${spec.label}` : '新建支付渠道'}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="保存"
      cancelText="取消"
      confirmLoading={save.isPending}
      width={620}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="channelType"
              label="渠道类型"
              rules={[{ required: true, message: '请选择渠道类型' }]}
              tooltip={isEdit ? '类型创建后不可修改，如需更换请删除后重建' : undefined}
            >
              <Select
                options={CHANNEL_TYPE_OPTIONS}
                disabled={isEdit}
                onChange={() => {
                  // 切换类型时清空所有动态字段，避免上一类型的值串入
                  form.resetFields(ALL_FIELD_KEYS);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="channelName" label="渠道名称" rules={[{ required: true, message: '请输入渠道名称' }]}>
              <Input placeholder="如：支付宝官方渠道" />
            </Form.Item>
          </Col>
        </Row>

        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={spec.description} />

        <Typography.Text strong style={{ fontSize: 13 }}>
          渠道参数
        </Typography.Text>
        <Row gutter={12} style={{ marginTop: 8 }}>
          {spec.fields.map(renderField)}
        </Row>

        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={8}>
            <Form.Item name="isActive" label="启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="sortOrder" label="排序" tooltip="数值越小越靠前">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="remark" label="备注">
              <Input placeholder="选填" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
